import React, { useState } from 'react';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import type { User } from 'firebase/auth';
import { auth } from '../firebase';
import { persistRedirectBase } from '../handoff';
import type { RedirectInfo } from '../handoff';
import type { LastProvider } from '../storage/savedProfile';

const GOOGLE_OAUTH_CLIENT_ID = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID as string;
const OAUTH_STATE_KEY = 'sf_oauth_state';
const OAUTH_NONCE_KEY = 'sf_oauth_nonce';

type HandleUserOpts = {
  remember?: boolean;
  provider?: LastProvider;
  profile?: { displayName?: string | null; photoURL?: string | null };
};

interface Props {
  onUser: (user: User, opts?: HandleUserOpts) => Promise<void>;
  onMfa: (error: unknown, opts: { remember: boolean; provider: LastProvider }) => void;
  onError: (message: string) => void;
  onSignInFailure: (message: string) => void;
  initialEmail?: string;
  loginHint?: string;
  redirectInfo?: RedirectInfo | null;
}

function formatError(err: unknown): string {
  const code = (err as { code?: string }).code ?? '';
  const map: Record<string, string> = {
    'auth/user-not-found': 'No account found with this email.',
    'auth/wrong-password': 'Incorrect password.',
    'auth/invalid-credential': 'Invalid email or password.',
    'auth/invalid-email': 'Invalid email address.',
    'auth/user-disabled': 'This account has been disabled.',
    'auth/too-many-requests': 'Too many failed attempts. Try again later.',
    'auth/network-request-failed': 'Network error. Check your connection.',
    'auth/popup-blocked': 'Popup blocked — allow popups and try again.',
    'auth/popup-closed-by-user': 'Sign-in was cancelled.',
  };
  return map[code] ?? (err instanceof Error ? err.message : 'Sign-in failed. Please try again.');
}

function Spinner() {
  return (
    <div className="h-4 w-4 animate-spin rounded-full border-2 border-sf-blue-dark border-t-transparent" />
  );
}

export function SignInPanel({ onUser, onMfa, onError, onSignInFailure, initialEmail, loginHint, redirectInfo }: Props) {
  const [tab, setTab] = useState<'google' | 'email'>('google');
  const [email, setEmail] = useState(initialEmail ?? '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [remember, setRemember] = useState(false);
  const unknownService = redirectInfo?.source === 'fallback';

  async function handleGoogle() {
    persistRedirectBase();
    await signOut(auth); // clear cached Firebase session before swapping users

    const state = crypto.randomUUID();
    const nonce = crypto.randomUUID();
    sessionStorage.setItem(OAUTH_STATE_KEY, state);
    sessionStorage.setItem(OAUTH_NONCE_KEY, nonce);

    const params = new URLSearchParams({
      client_id: GOOGLE_OAUTH_CLIENT_ID,
      redirect_uri: window.location.origin + '/',
      response_type: 'id_token',
      scope: 'openid email profile',
      hd: 'sellersflow.com',
      prompt: 'select_account',
      nonce,
      state,
    });
    if (loginHint) params.set('login_hint', loginHint);

    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
  }

  async function handleEmail(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      await onUser(result.user, { remember, provider: 'password' });
    } catch (err: unknown) {
      const code = (err as { code?: string }).code;
      if (code === 'auth/multi-factor-auth-required') {
        onMfa(err, { remember, provider: 'password' });
      } else if (code === 'auth/user-disabled' || code === 'auth/network-request-failed') {
        onError(formatError(err));
      } else {
        onSignInFailure(formatError(err));
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Sub-heading */}
      <p className="text-center text-sm text-gray-500 dark:text-gray-400">
        {redirectInfo && redirectInfo.source !== 'fallback' ? (
          <>
            Signing in to{' '}
            <span className="font-semibold text-sf-blue-dark dark:text-white">
              {redirectInfo.label}
            </span>
          </>
        ) : (
          'Sign in with your team account'
        )}
      </p>

      {/* Tab switcher */}
      <div className="flex rounded-xl bg-gray-100 p-1 dark:bg-white/10">
        {(['google', 'email'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            disabled={unknownService}
            className={`flex-1 rounded-lg py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${
              tab === t
                ? 'bg-white text-sf-blue-dark shadow dark:bg-white/20 dark:text-white'
                : 'text-gray-500 hover:text-sf-blue-dark dark:text-gray-400 dark:hover:text-white'
            }`}
          >
            {t === 'google' ? 'Google' : 'Email & Password'}
          </button>
        ))}
      </div>

      {/* Google */}
      {tab === 'google' && (
        <button
          onClick={handleGoogle}
          disabled={unknownService}
          className="flex w-full items-center justify-center gap-3 rounded-xl bg-sf-blue-vibrant px-6 py-3 font-medium text-white shadow-lg shadow-sf-blue-vibrant/20 transition hover:bg-sf-blue-vibrant-dark disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-sf-blue-vibrant"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#fff" />
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#fff" />
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#fff" />
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#fff" />
          </svg>
          Continue with Google
        </button>
      )}

      {/* Email & Password */}
      {tab === 'email' && (
        <form onSubmit={handleEmail} className="flex flex-col gap-3">
          <input
            type="email"
            placeholder="name@sellersflow.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-sf-blue-dark placeholder-gray-400 outline-none focus:border-sf-blue-vibrant dark:border-white/20 dark:bg-white/10 dark:text-white dark:placeholder-gray-500"
          />
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 pr-11 text-sm text-sf-blue-dark placeholder-gray-400 outline-none focus:border-sf-blue-vibrant dark:border-white/20 dark:bg-white/10 dark:text-white dark:placeholder-gray-500"
            />
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-gray-400 hover:text-sf-blue-dark dark:text-gray-500 dark:hover:text-white"
            >
              {showPassword ? (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12c1.292 4.338 5.31 7.5 10.066 7.5.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.243 4.243L9.88 9.88" />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              )}
            </button>
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 select-none">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-sf-blue-vibrant focus:ring-sf-blue-vibrant"
            />
            Remember me on this device
          </label>
          <button
            type="submit"
            disabled={loading || unknownService}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-sf-blue-vibrant py-3 font-medium text-white transition hover:bg-sf-blue-vibrant-dark disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading && <Spinner />}
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      )}

      <p className="text-center text-xs text-gray-400">
        Internal use — team members only
      </p>
    </div>
  );
}
